import { Input, makeStyles, mergeClasses, shorthands, tokens } from '@fluentui/react-components';
import { BinRecycle20Regular, BinRecycle24Regular, Checkmark20Regular, Dismiss20Regular, Pen20Regular, Pen24Regular, Pin20Regular, Pin24Regular } from '@fluentui/react-icons';
import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../../Hooks/useResponsive';
import { EllipsisText } from '../Common/EllipsisText';
import { GptIcon } from '../Common/GptIcon';

interface ConversationMenuItemProps {
    title: string;
    id: string;
    selected: boolean;
    gptId: string | undefined;
    isPinned?: boolean;
    onPinToggle?: (id: string, pinned: boolean) => void;
    onRemoveClick?: (id: string) => void;
    onEditClick?: (id: string) => void;
    onTitleChange?: (id: string, title: string) => void;
    onSelect: (id: string) => void | Promise<void>;
}

const selectedItemStyle = {
    backgroundColor: tokens.colorNeutralBackground1Hover,
    cursor: 'pointer',
    ...shorthands.borderRadius(tokens.borderRadiusLarge),
};

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'row',
        ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
        columnGap: tokens.spacingHorizontalS,
        ':hover': selectedItemStyle,
    },
    selected: selectedItemStyle,
    commands: {
        display: 'flex',
        flexDirection: 'row',
        columnGap: '12px',
        justifyContent: 'flex-end',
        alignItems: 'center',
    },
    icon: {
        color: tokens.colorNeutralForeground1Hover,
        ':hover': {
            color: tokens.colorBrandForegroundInverted,
        },
        alignSelf: 'center'
    },
    subIcon: {
        color: tokens.colorNeutralForeground1Hover,
        height: '16px',
        backgroundColor: tokens.colorBrandForeground1,
        ...shorthands.borderRadius('5px'),
        boxShadow: ' rgba(0, 0, 0, 0.19) 0px 10px 20px, rgba(0, 0, 0, 0.23) 0px 6px 6px;',
    },
    title: {
        ...shorthands.overflow('hidden'),
        textOverflow: 'ellipsis',
        ...shorthands.flex(1),
        display: 'block',
        whiteSpace: 'nowrap',
        alignItems: 'center',
    },
});

const ConversationMenuItem: FC<ConversationMenuItemProps> = (props) => {
    const { id, onEditClick, onRemoveClick, onTitleChange, onSelect, selected, title } = props;
    const classes = useStyles();
    const { t } = useTranslation();
    const isMobile = useIsMobile();
    const [editMode, setEditMode] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [localTitle, setLocalTitle] = useState(title);
    const [isLoading, setIsLoading] = useState(false);
    const titleInputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // On mobile, buttons should always be visible; on desktop, only on hover
    const shouldShowButtons = isMobile || hovered;

    // Handle click outside to close edit mode
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (editMode && containerRef.current && !containerRef.current.contains(event.target as Node)) {
                // Save the changes when clicking outside (only if title is not empty)
                if (localTitle.trim().length > 0 && localTitle !== '\n') {
                    handleConfirmEdit(id);
                } else {
                    handleDismissEdit();
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [editMode, localTitle, id]);

    const handleEditClick = (id: string) => {
        setEditMode(!editMode);
        if (onEditClick) {
            onEditClick(id);
        }
    };

    const handleRemoveClick = (id: string) => {
        if (onRemoveClick) {
            onRemoveClick(id);
        }
    };

    const handleDismissEdit = () => {
        setEditMode(false);
        setLocalTitle(title);
    };

    const handleLocalTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLocalTitle(e.target.value);
    };

    const handleConfirmEdit = (id: string) => {
        setEditMode(false);
        if (onTitleChange) {
            onTitleChange(id, localTitle);
        }
    };

    const handleDoubleClick = () => {
        // Prevent double-click from triggering edit mode - we handle this in onClick now
        return;
    };

    const handleClick = async () => {
        if (editMode || isLoading) {
            return;
        }

        // Clear any existing timeout
        if (clickTimeoutRef.current) {
            clearTimeout(clickTimeoutRef.current);
            clickTimeoutRef.current = null;
        }

        // If the item is not selected, select it first
        if (!selected) {
            try {
                setIsLoading(true);
                await onSelect(id);
            } finally {
                setIsLoading(false);
            }
        } else {
            // Item is already selected - second click should enter edit mode
            // Add a small delay to prevent conflict with double-click events
            clickTimeoutRef.current = setTimeout(() => {
                setEditMode(true);
                clickTimeoutRef.current = null;
            }, 100);
        }
    };
    useEffect(() => {
        setLocalTitle(title);
    }, [title]);

    useEffect(() => {
        if (editMode) {
            titleInputRef.current?.focus();
        }
    }, [editMode]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (clickTimeoutRef.current) {
                clearTimeout(clickTimeoutRef.current);
            }
        };
    }, []);

    return (
        <div
            ref={containerRef}
            className={mergeClasses(classes.root, selected ? classes.selected : '')}
            onMouseOver={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onClick={handleClick}
            onDoubleClick={handleDoubleClick}
            style={{ opacity: isLoading ? 0.6 : 1, cursor: isLoading ? 'wait' : 'pointer' }}
        >
            <GptIcon gptId={props.gptId} theme={'light'} />
            {!editMode && (
                <>
                    <EllipsisText hover={hovered}>{localTitle}</EllipsisText>
                    {/* Always render the commands container to reserve space */}
                    <div className={classes.commands} style={{ minWidth: 64 /* room for 3 larger icons */ }}>
                        {shouldShowButtons ? (
                            <>
                                {/* Pin / unpin button (larger) */}
                                <Pin24Regular
                                    className={classes.icon}
                                    style={{ transform: props.isPinned ? 'rotate(-20deg)' : undefined, color: props.isPinned ? tokens.colorBrandForeground1 : undefined, fontSize: '20px', width: '20px', height: '20px' }}
                                    onClick={(ev) => {
                                        ev.stopPropagation();
                                        props.onPinToggle && props.onPinToggle(props.id, !props.isPinned);
                                    }}
                                />
                                <Pen24Regular
                                    className={classes.icon}
                                    style={{ fontSize: '20px', width: '20px', height: '20px' }}
                                    onClick={(ev) => {
                                        ev.stopPropagation();
                                        handleEditClick(id);
                                    }}
                                />
                                <BinRecycle24Regular
                                    className={classes.icon}
                                    style={{ fontSize: '20px', width: '20px', height: '20px' }}
                                    onClick={(ev) => {
                                        ev.stopPropagation();
                                        handleRemoveClick(id);
                                    }}
                                />
                            </>
                        ) : null}
                    </div>
                </>
            )}
            {editMode && (
                <>
                    <div className="ellipsisText">
                        <input
                            ref={titleInputRef}
                            value={localTitle}
                            onChange={(e) => handleLocalTitleChange(e)}
                            onKeyUp={(e) => {
                                if (e.key === 'Enter' && e.shiftKey === false) {
                                    if (localTitle.trim().length > 0 && localTitle !== '\n') {
                                        handleConfirmEdit(id);
                                    }
                                } else if (e.key === 'Escape') {
                                    handleDismissEdit();
                                }
                            }}
                            style={{
                                border: 'none',
                                background: 'transparent',
                                outline: 'none',
                                padding: '0',
                                margin: '0',
                                fontSize: 'inherit',
                                lineHeight: 'inherit',
                                fontFamily: 'inherit',
                                color: 'inherit',
                                boxShadow: 'none',
                                width: '100%',
                                display: 'block',
                                textAlign: 'start',
                                verticalAlign: 'baseline',
                            }}
                        />
                    </div>
                    <Checkmark20Regular
                        className={classes.icon}
                        style={{ fontSize: '28px', width: '28px', height: '27px' }}
                        onClick={(ev) => {
                            ev.stopPropagation();
                            handleConfirmEdit(id);
                        }}
                    />
                    <Dismiss20Regular
                        className={classes.icon}
                        style={{ fontSize: '28px', width: '28px', height: '27px' }}
                        onClick={(ev) => {
                            ev.stopPropagation();
                            handleDismissEdit();
                        }}
                    />
                </>
            )}
        </div>
    );
};

export default ConversationMenuItem;
